import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useMaintenanceStatus } from "@/hooks/useMaintenanceStatus";
import { MaintenancePage } from "@/components/maintenance/MaintenancePage";
import type { OrgUserRole } from "@/types/firestore";

interface AuthGuardProps {
  allowedRoles?: OrgUserRole[];
}

export function AuthGuard({ allowedRoles }: AuthGuardProps) {
  const { user, orgUser, role, realRole, loading, error } = useAuth();
  const maintenance = useMaintenanceStatus(role, orgUser?.orgId);

  if (loading || maintenance.loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="font-body text-text-muted">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="max-w-md rounded-lg border border-orange-red/30 bg-orange-red/5 p-6 text-center">
          <h2 className="mb-2 font-heading text-lg font-semibold text-orange-red">
            Access Denied
          </h2>
          <p className="font-body text-text">{error}</p>
        </div>
      </div>
    );
  }

  const isPlatform =
    realRole === "PLATFORM_OWNER" || realRole === "PLATFORM_ADMIN";

  if (!role || (allowedRoles && !allowedRoles.includes(role) && !isPlatform)) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="max-w-md rounded-lg border border-gold/30 bg-gold/5 p-6 text-center">
          <h2 className="mb-2 font-heading text-lg font-semibold text-gold">
            Insufficient Permissions
          </h2>
          <p className="font-body text-text">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  // Maintenance check — blocks before any routes/data render
  if (maintenance.blocked && maintenance.scope) {
    return (
      <MaintenancePage
        message={maintenance.message}
        estimatedEnd={maintenance.estimatedEnd}
        contactEmail={maintenance.contactEmail}
        scope={maintenance.scope}
        orgName={maintenance.orgName}
      />
    );
  }

  return <Outlet />;
}
